import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartColumn } from "lucide-react";
import { PageHeader, ToggleGroup, pageLayoutClass } from "@/components/ui";
import { apiClient } from "@/lib/api";
import {
  DEFAULT_DEVICE_STATS_RANGE,
  DEVICE_STATS_RANGES,
  POSTHOG_DEVICE_STATS_IFRAME_NAME,
  deviceStatsEmbedApiPath,
  type DeviceStatsRange,
} from "@/lib/posthogDeviceStatsEmbed";
import { cn } from "@/lib/utils";
import { useThemeStore } from "@/store/themeStore";

const DEFAULT_IFRAME_HEIGHT_PX = 900;

type PosthogDimensionsMessage = {
  event: "posthog:dimensions";
  name: string;
  height: number;
};

function isPosthogDimensionsMessage(
  data: unknown,
): data is PosthogDimensionsMessage {
  if (data === null || typeof data !== "object") {
    return false;
  }
  const record = data as Record<string, unknown>;
  return (
    record.event === "posthog:dimensions" &&
    typeof record.name === "string" &&
    typeof record.height === "number" &&
    Number.isFinite(record.height) &&
    record.height > 0
  );
}

type AdminDeviceStatsPageProps = {
  /** When true, omit page chrome (used as a Devices tab panel). */
  embedded?: boolean;
};

export default function AdminDeviceStatsPage({
  embedded = false,
}: AdminDeviceStatsPageProps) {
  const { t } = useTranslation("adminDeviceStats");
  const resolvedTheme = useThemeStore((s) => s.resolved);
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT_PX);
  const [range, setRange] = useState<DeviceStatsRange>(DEFAULT_DEVICE_STATS_RANGE);
  const [embedSrc, setEmbedSrc] = useState<string | null>(null);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (!isPosthogDimensionsMessage(event.data)) {
        return;
      }
      if (event.data.name !== POSTHOG_DEVICE_STATS_IFRAME_NAME) {
        return;
      }
      setIframeHeight(Math.ceil(event.data.height));
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setIframeHeight(DEFAULT_IFRAME_HEIGHT_PX);
    setEmbedSrc(null);

    void (async () => {
      const { data: html } = await apiClient.get<string>(
        deviceStatsEmbedApiPath(range, resolvedTheme),
      );
      if (cancelled || typeof html !== "string") {
        return;
      }
      objectUrl = URL.createObjectURL(
        new Blob([html], { type: "text/html;charset=utf-8" }),
      );
      if (cancelled) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      setEmbedSrc(objectUrl);
    })();

    return () => {
      cancelled = true;
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [range, resolvedTheme]);

  const stats = (
    <div className="flex min-w-0 flex-col gap-4">
      <ToggleGroup
        variant="segmented-neutral"
        size="sm"
        className="self-start"
        value={range}
        onChange={setRange}
        ariaLabel={t("rangeLabel")}
        options={DEVICE_STATS_RANGES.map((value) => ({
          value,
          label: t(`ranges.${value}`),
        }))}
      />
      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-card">
        {embedSrc ? (
          <iframe
            name={POSTHOG_DEVICE_STATS_IFRAME_NAME}
            title={t("iframeTitle")}
            src={embedSrc}
            className="w-full border-0"
            style={{ height: iframeHeight }}
            allowFullScreen
          />
        ) : (
          <div
            className="w-full"
            style={{ height: iframeHeight }}
            aria-busy="true"
            aria-label={t("iframeTitle")}
          />
        )}
      </div>
    </div>
  );

  if (embedded) {
    return stats;
  }

  return (
    <div className={cn(pageLayoutClass, "min-h-0")}>
      <PageHeader
        title={t("title")}
        description={t("description")}
        icon={ChartColumn}
      />
      {stats}
    </div>
  );
}
